// src/firebase/config.js
// ─────────────────────────────────────────────────────────────────────────────
// Phase 2: Firebase Auth added
// Phase 3: Firebase Cloud Messaging (FCM) added
// ─────────────────────────────────────────────────────────────────────────────

import { initializeApp }  from "firebase/app";
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

// Set REACT_APP_USE_FIREBASE_EMULATOR=true in .env.local to run the app
// against the local Firestore emulator (localhost:8080). When the flag is on,
// Firestore uses an isolated in-memory cache so emulator data can NEVER mix
// with or pollute the IndexedDB cache used by your real production data.
const USE_EMULATOR =
  process.env.REACT_APP_USE_FIREBASE_EMULATOR === "true";

// Persistent (IndexedDB) cache: when a page reloads, onSnapshot listeners
// resume from the local cache and Firestore bills only the documents that
// changed (if reattached within ~30 min) instead of re-reading the whole
// result set. This is what makes repeated admin reloads / hot reloads cheap.
// Browsers without IndexedDB fall back to the in-memory cache automatically.
// NOTE: the persistent cache is intentionally DISABLED in emulator mode so the
// emulator (same project id) can't mix with production's local cache.
export const db = initializeFirestore(app, {
  localCache: USE_EMULATOR
    ? undefined
    : persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

// Point the Firestore client at the local emulator when the flag is set.
// All reads/writes go ONLY to localhost:8080 — production cloud data is untouched.
if (USE_EMULATOR) {
  connectFirestoreEmulator(db, "localhost", 8080);
  console.log("[Firestore] Connected to local emulator on localhost:8080");
}

export const auth = getAuth(app);

// Point Auth at its local emulator when the flag is set, so the seeded
// emulator admin user can actually sign in. Harmless when the flag is off.
if (USE_EMULATOR) {
  connectAuthEmulator(auth, "http://localhost:9099");
}

// ─── FCM (Firebase Cloud Messaging) ───────────────────────────────────────────
let messagingInstance = null;
let messagingInitPromise = null;

/** Cached FCM service worker registration, set by registerFcmSw().
 *  Used by requestFcmToken() so Firebase's getToken() subscribes via the
 *  correct SW, avoiding "no active Service Worker" errors. */
let fcmSwRegistration = null;

/**
 * Wait for a service worker registration to reach the "activated" state.
 * Resolves once the registration's active worker is confirmed.
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<ServiceWorkerRegistration>}
 */
function waitForActive(registration) {
  return new Promise((resolve) => {
    if (registration.active) return resolve(registration);
    // Listen for state changes on the installing/waiting worker
    const sw = registration.installing || registration.waiting;
    if (!sw) return resolve(registration); // fallback
    sw.addEventListener("statechange", function onState() {
      if (sw.state === "activated") {
        resolve(registration);
      }
    });
  });
}

/**
 * Lazily initialise Messaging (only supported on HTTPS/localhost).
 * Returns null if FCM is not supported in this environment.
 */
async function ensureMessaging() {
  if (messagingInstance) return messagingInstance;
  if (messagingInitPromise) return messagingInitPromise;

  messagingInitPromise = (async () => {
    const supported = await isSupported();
    if (!supported) {
      console.warn("[FCM] Not supported in this environment");
      return null;
    }
    messagingInstance = getMessaging(app);
    return messagingInstance;
  })();

  return messagingInitPromise;
}

/**
 * Request notification permission and obtain an FCM push token.
 * @param {string} vapidKey - Optional VAPID key for web push (from Firebase Console).
 * @returns {Promise<string|null>} The FCM token, or null if denied/unsupported.
 */
export async function requestFcmToken(vapidKey) {
  const messaging = await ensureMessaging();
  if (!messaging) return null;

  try {
    const permission = await Notification.requestPermission();
    console.log("[FCM] Notification permission:", permission);
    if (permission !== "granted") {
      console.warn("[FCM] Notification permission denied");
      return null;
    }

    // Ensure we have a valid SW registration to pass to getToken.
    // If fcmSwRegistration is not yet set, fall back to the default ready promise.
    let swReg = fcmSwRegistration;
    if (!swReg || !swReg.active) {
      swReg = await navigator.serviceWorker.ready;
    }

    const token = await getToken(messaging, {
      vapidKey: vapidKey || process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    console.log("[FCM] Token obtained:", token);
    return token;
  } catch (err) {
    console.error("[FCM] Error getting token:", err);
    return null;
  }
}

/**
 * Subscribe to foreground push messages.
 * @param {Function} callback - Called with (payload) when a message arrives.
 * @returns {Function|null} Unsubscribe function, or null if unsupported.
 */
export async function onForegroundMessage(callback) {
  const messaging = await ensureMessaging();
  if (!messaging) return null;

  return onMessage(messaging, (payload) => {
    console.log("[FCM] Foreground message received:", payload);
    callback(payload);
  });
}

/**
 * Register the FCM service worker, passing Firebase config via query params.
 * Waits for the service worker to be fully activated before returning.
 */
export async function registerFcmSw() {
  if (!("serviceWorker" in navigator)) return null;

  // Build query string with Firebase config values
  const params = new URLSearchParams({
    apiKey:            firebaseConfig.apiKey,
    authDomain:        firebaseConfig.authDomain,
    projectId:         firebaseConfig.projectId,
    storageBucket:     firebaseConfig.storageBucket,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId:             firebaseConfig.appId,
    measurementId:     firebaseConfig.measurementId || "",
  });
  const swUrl = `/firebase-messaging-sw.js?${params.toString()}`;

  try {
    // Check if an FCM service worker is already registered
    const registrations = await navigator.serviceWorker.getRegistrations();
    const existing = registrations.find(
      r => r.scriptURL?.includes("firebase-messaging-sw.js")
    );

    let reg;
    if (existing) {
      reg = existing;
      console.log("[FCM] Service worker already registered");
      // If the worker is still installing/waiting, wait for activation
      await waitForActive(reg);
    } else {
      // Register and wait for it to become active
      await navigator.serviceWorker.register(swUrl);
      reg = await navigator.serviceWorker.ready;
      console.log("[FCM] Service worker registered and active:", reg.scope);
    }

    // Cache the registration so requestFcmToken can pass it to getToken()
    fcmSwRegistration = reg;
    return reg;
  } catch (err) {
    console.error("[FCM] Service worker registration failed:", err);
    return null;
  }
}

export default app;

